import React from 'react';
import { Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { ExternalLink, ArrowRight, Bike, Car, Shield, GraduationCap, Users, Sparkles } from 'lucide-react';

export default function NosPlateformesPage() {
  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12">
        <SectionTitle
          kicker="L'écosystème LLB ECSR"
          title="Deux plateformes, une même mission : éduquer à la route"
          sub="Code Voiture et Code 2 Roues sont conçus par Bruno LLORENS, Enseignant de la Conduite et de la Sécurité Routière diplômé d'État. Deux univers pédagogiques indépendants, gratuits, sans publicité — pour préparer chaque type de permis avec le même sérieux."
        />

        {/* Two-column cross-linking */}
        <div className="mt-10 grid md:grid-cols-2 gap-4 sm:gap-6">
          {/* Code Voiture */}
          <div className="rounded-2xl border border-red-700/40 bg-gradient-to-br from-[#1a0a0d] via-[#0e0709] to-[#0d0d0e] p-6 sm:p-8 flex flex-col" data-testid="platform-card-voiture">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-600 flex items-center justify-center">
                <Car size={22} className="text-white" />
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-widest font-mono-spec text-red-400 font-bold">Vous êtes ici</div>
                <div className="text-white font-serif-editorial text-[22px] sm:text-[26px] leading-tight">Code Voiture</div>
              </div>
            </div>
            <p className="text-white/70 text-[13.5px] sm:text-[14.5px] leading-relaxed mb-4">
              Préparation gratuite au <strong className="text-white">permis B</strong> et à toutes ses extensions : conduite accompagnée (AAC), conduite supervisée (CS), BEA (boîte auto), B96, B+E. Magazine éditorial, cours REMC, simulateurs, démos interactives.
            </p>
            <ul className="space-y-2 mb-5 text-white/60 text-[13px]">
              <li className="flex items-center gap-2"><Sparkles size={13} className="text-red-400" /> 38 leçons REMC pas à pas</li>
              <li className="flex items-center gap-2"><Sparkles size={13} className="text-red-400" /> 30+ articles magazine originaux</li>
              <li className="flex items-center gap-2"><Sparkles size={13} className="text-red-400" /> 8 démos pédagogiques interactives</li>
              <li className="flex items-center gap-2"><Sparkles size={13} className="text-red-400" /> Suivi enseignant / élève intégré</li>
            </ul>
            <div className="mt-auto flex flex-wrap gap-2">
              <Link to="/eleve" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold" data-testid="platform-voiture-eleve">
                Espace élève <ArrowRight size={13} />
              </Link>
              <Link to="/enseignant" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-white/15 text-white/85 hover:text-white text-[13px] font-semibold" data-testid="platform-voiture-enseignant">
                Espace enseignant
              </Link>
            </div>
          </div>

          {/* Code 2 Roues */}
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0a0d1a] via-[#07070e] to-[#0d0d0e] p-6 sm:p-8 flex flex-col" data-testid="platform-card-2roues">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-sky-500 flex items-center justify-center">
                <Bike size={22} className="text-white" />
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-widest font-mono-spec text-sky-300 font-bold">Notre autre plateforme</div>
                <div className="text-white font-serif-editorial text-[22px] sm:text-[26px] leading-tight">Code 2 Roues</div>
              </div>
            </div>
            <p className="text-white/70 text-[13.5px] sm:text-[14.5px] leading-relaxed mb-4">
              Préparation gratuite aux permis <strong className="text-white">AM (BSR), A1, A2, A</strong> et passerelles associées. Magazine dédié aux deux-roues motorisés, cours REMC-2R adaptés, focus équipement et trajectoires.
            </p>
            <ul className="space-y-2 mb-5 text-white/60 text-[13px]">
              <li className="flex items-center gap-2"><Sparkles size={13} className="text-sky-400" /> Cours dédiés 50 cc / 125 cc / grosse cylindrée</li>
              <li className="flex items-center gap-2"><Sparkles size={13} className="text-sky-400" /> Trajectoires de sécurité et équipement</li>
              <li className="flex items-center gap-2"><Sparkles size={13} className="text-sky-400" /> Magazine enquêtes 2 roues</li>
              <li className="flex items-center gap-2"><Sparkles size={13} className="text-sky-400" /> Suivi enseignant / élève intégré</li>
            </ul>
            <div className="mt-auto flex flex-wrap gap-2">
              <a href="https://code2roues.fr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-sky-500 hover:bg-sky-400 text-black text-[13px] font-semibold" data-testid="platform-2roues-open">
                Ouvrir code2roues.fr <ExternalLink size={13} />
              </a>
            </div>
          </div>
        </div>

        {/* Common mission block */}
        <div className="mt-8 sm:mt-10 rounded-2xl border border-white/10 bg-[#0d0d0e] p-6 sm:p-8" data-testid="mission-commune">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
              <Shield size={20} className="text-white/80" />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-widest font-mono-spec text-white/50 font-bold mb-1">La marque LLB ECSR</div>
              <h3 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight mb-3">Un collectif d'éducation routière indépendant</h3>
              <p className="text-white/70 text-[13.5px] sm:text-[14.5px] leading-relaxed mb-3">
                <strong className="text-white">LLB ECSR</strong> — pour Bruno LLORENS, Enseignant de la Conduite et de la Sécurité Routière diplômé d'État — porte les deux plateformes avec la même exigence : contenu éditorial original, sourcé, pédagogique, <em>sans</em> publicité, <em>sans</em> revente de données, <em>sans</em> objectif commercial. Un contenu utile aux candidats et aux moniteurs, un point c'est tout.
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3 mt-4 text-[11.5px] sm:text-[12px] font-mono-spec">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-white/70">
                  <GraduationCap size={11} /> ECSR diplômé d'État
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-white/70">
                  <Users size={11} /> Deux plateformes complémentaires
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-white/70">
                  100 % gratuit
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-white/70">
                  Sans publicité
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Complementarity block */}
        <div className="mt-8 sm:mt-10 rounded-2xl border border-red-700/25 bg-red-950/10 p-5 sm:p-7" data-testid="complementarite">
          <h3 className="font-serif-editorial text-white text-[18px] sm:text-[22px] leading-tight mb-3">Élève voiture ? Pense aussi au permis 2 roues.</h3>
          <p className="text-white/70 text-[13.5px] leading-relaxed">
            À partir de <strong className="text-white">14 ans</strong>, tu peux passer le <strong className="text-white">BSR (AM)</strong> pour un cyclomoteur 50 cc. Une fois ton permis B en poche, tu peux ajouter la <strong className="text-white">formation 125 cc (7 h)</strong> après 2 ans, ou viser un permis moto complet (A1, A2, A). Les deux plateformes sont pensées pour se compléter : maîtriser la voiture ET les deux-roues, c'est doubler ta lecture de la route.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <Link to="/permis-125" className="inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 text-[13px] font-semibold" data-testid="complementarite-125-link">
              Rouler en 125 avec le permis B <ArrowRight size={13} />
            </Link>
            <a href="https://code2roues.fr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sky-400 hover:text-sky-300 text-[13px] font-semibold" data-testid="complementarite-link">
              Explorer code2roues.fr <ExternalLink size={13} />
            </a>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
